import { Routes } from '@angular/router';
import { permissionsGuard } from './core/guards/permissions.guard';
import { b2bPortalAuthGuard } from './core/guards/b2b-portal-auth.guard';
import { b2b2FaSessionGuard } from './core/guards/b2b-2fa-session.guard';
import { PRIVILEGES_PER_FEATURE } from '@core/constants/privileges-per-feature.const';
import { environment } from '@env/environment';
import { APP_ROUTES } from '@core/constants/app-routes.const';
import { unsavedChangesGuard } from '@core/guards/unsaved-changes.guard';
import { msalGuard } from '@core/guards/msal.guard';
import { portalAccessGuard } from '@core/guards/portal-access.guard';
import { agentFrontendEntityResolver } from '@core/resolvers/agent-frontend-entity.resolver';
import { customerIdNormalizerGuard } from '@core/guards/customer-id-normalizer.guard';

export const routes: Routes = [
  {
    path: '',
    redirectTo: environment.loginRoute,
    pathMatch: 'full',
  },
  {
    path: APP_ROUTES.LOGIN,
    canActivate: [portalAccessGuard],
    loadComponent: () =>
      import('@core/components/login/login-shell/login-shell.component').then(
        m => m.LoginShellComponent
      ),
  },
  {
    path: APP_ROUTES.FAILED_LOGIN,
    canActivate: [portalAccessGuard],
    loadComponent: () =>
      import('@core/components/login/failed-login/failed-login.component').then(
        m => m.FailedLoginComponent
      ),
  },
  {
    path: APP_ROUTES.LOGGING_OUT,
    canActivate: [portalAccessGuard],
    loadComponent: () =>
      import('@core/components/login/logging-out/logging-out.component').then(
        m => m.LoggingOutComponent
      ),
  },
  {
    path: APP_ROUTES.CODE_VERIFICATION,
    loadComponent: () =>
      import('@core/components/login/code-verification/code-verification.component').then(
        m => m.CodeVerificationComponent
      ),
    canActivate: [portalAccessGuard, b2b2FaSessionGuard],
  },
  {
    path: APP_ROUTES.NO_EMAIL_FOUND,
    canActivate: [portalAccessGuard],
    loadComponent: () =>
      import('@core/components/login/no-email-found/no-email-found.component').then(
        m => m.NoEmailFoundComponent
      ),
  },
  {
    path: '',
    loadComponent: () =>
      import('@core/components/app-shell/app-shell.component').then(
        m => m.AppShellComponent
      ),
    children: [
      {
        path: APP_ROUTES.USERS,
        canActivate: [portalAccessGuard, msalGuard, permissionsGuard],
        loadComponent: () =>
          import('@features/admin-console/users/users-page/users-page.component').then(
            m => m.UsersPageComponent
          ),
      },
      {
        path: `${APP_ROUTES.PRODUCT_MANAGEMENT}/user/:userId/customer/:customerNumber/entity/:entity`,
        canActivate: [portalAccessGuard],
        loadComponent: () =>
          import('@features/admin-console/product-management/product-management.component').then(
            m => m.ProductManagementComponent
          ),
      },
      {
        path: APP_ROUTES.THREATS,
        canActivate: [portalAccessGuard, msalGuard, permissionsGuard],
        loadComponent: () =>
          import('@features/admin-console/threats/threat-list/api-threats-list.component').then(
            m => m.ApiThreatsListComponent
          ),
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.ApiThreats,
        },
      },
      {
        path: APP_ROUTES.THREATS_BY_PRINCIPLE,
        canActivate: [portalAccessGuard],
        loadComponent: () =>
          import('@features/admin-console/threats/threat-list-by-principle/threat-list-by-principle.component').then(
            m => m.ThreatListByPrincipleComponent
          ),
      },
      {
        path: APP_ROUTES.FEATURE_FLAGS,
        canActivate: [portalAccessGuard, msalGuard, permissionsGuard],
        loadComponent: () =>
          import('@features/admin-console/feature-flags/app-feature-flags/app-feature-flags.component').then(
            m => m.AppFeatureFlagsComponent
          ),
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.FeatureFlags,
        },
      },
      {
        path: APP_ROUTES.SHIELD,
        canActivate: [portalAccessGuard, msalGuard, permissionsGuard],
        loadComponent: () =>
          import('@features/admin-console/shield/shell/shield.component').then(
            m => m.ShieldComponent
          ),
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.Shield,
        },
      },
      {
        path: `${APP_ROUTES.CUSTOMER_LOGS_SEARCH}/:userId`,
        canActivate: [portalAccessGuard, msalGuard, permissionsGuard],
        loadComponent: () =>
          import('@features/admin-console/customer-logs/customer-logs-search/customer-logs-search.component').then(
            m => m.CustomerLogsSearchComponent
          ),
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.FrontendEvents,
        },
      },
      {
        path: `${APP_ROUTES.CUSTOMER_LOG}/:logId`,
        canActivate: [portalAccessGuard],
        loadComponent: () =>
          import('@features/admin-console/customer-logs/customer-log-record/customer-log-record.component').then(
            m => m.CustomerLogRecordComponent
          ),
      },
      {
        path: APP_ROUTES.DISPOSER_ACTION_DETAILS,
        canActivate: [portalAccessGuard, msalGuard, permissionsGuard],
        loadComponent: () =>
          import('@features/admin-console/users/disposer-action-details/disposer-action-details.component').then(
            m => m.DisposerActionDetailsComponent
          ),
      },
      {
        path: `${APP_ROUTES.CUSTOMER_AUDIT_LOG}/:logId`,
        canActivate: [portalAccessGuard],
        loadComponent: () =>
          import('@features/admin-console/customer-logs/audit-log-record/audit-log-record.component').then(
            m => m.AuditLogRecordComponent
          ),
      },
      {
        path: `${APP_ROUTES.CUSTOMER_AUDIT_LOGS_SEARCH}/:userId`,
        canActivate: [portalAccessGuard, msalGuard, permissionsGuard],
        loadComponent: () =>
          import('@features/admin-console/customer-logs/customer-audit-logs-search/customer-audit-logs-search.component').then(
            m => m.CustomerAuditLogsSearchComponent
          ),
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.UserAuditLogs,
        },
      },
      {
        path: `${APP_ROUTES.FEATURE_FLAGS}/os/:os/app/:app/feature/:feature`,
        canActivate: [portalAccessGuard],
        loadComponent: () =>
          import('@features/admin-console/feature-flags/manage-feature-flags/manage-feature-flags.component').then(
            m => m.ManageFeatureFlagsComponent
          ),
      },

      {
        path: `${APP_ROUTES.USERS}/:userId/entity/:entity`,
        canActivate: [portalAccessGuard, permissionsGuard, msalGuard],
        loadComponent: () =>
          import('@features/admin-console/users/user-details/user-details.component').then(
            m => m.UserDetailsComponent
          ),
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.Users.Details,
        },
      },
      {
        path: `${APP_ROUTES.USERS}/:userId/entity/:entity/devices`,
        canActivate: [portalAccessGuard],
        loadComponent: () =>
          import('@features/admin-console/users/user-devices/user-devices.component').then(
            m => m.UserDevicesComponent
          ),
      },
      {
        path: `${APP_ROUTES.NOTIFICATIONS}/banners/templates`,
        canActivate: [portalAccessGuard, msalGuard, permissionsGuard],
        loadComponent: () =>
          import('@features/admin-console/notifications/banners-list/banners-list.component').then(
            m => m.BannersListComponent
          ),
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.Banners,
        },
      },
      {
        path: `${APP_ROUTES.NOTIFICATIONS}/banners/templates/:templateId`,
        canActivate: [portalAccessGuard],
        loadComponent: () =>
          import('@features/admin-console/notifications/banner-details/banner-details.component').then(
            m => m.BannerDetailsComponent
          ),
      },
      {
        path: APP_ROUTES.APP_INFO_BANNER,
        canActivate: [portalAccessGuard, msalGuard, permissionsGuard],
        loadComponent: () =>
          import('@features/admin-console/app-info-banner/app-info-banner.component').then(
            m => m.AppInfoBannerComponent
          ),
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.AppMaintenance,
        },
      },
      {
        path: APP_ROUTES.END_OF_WORLD,
        canActivate: [portalAccessGuard, msalGuard, permissionsGuard],
        loadComponent: () =>
          import('@features/admin-console/end-of-world-banner/components/banner-wrapper/banner-wrapper.component').then(
            m => m.BannerWrapperComponent
          ),
        data: {
          requiredPermission: null,
          requiredPrivileges: PRIVILEGES_PER_FEATURE.EndOfWorldBanner,
        },
      },
      {
        path: APP_ROUTES.PUSH_NOTIFICATIONS,
        canActivate: [portalAccessGuard, msalGuard, permissionsGuard],
        data: {
          requiredPrivileges:
            PRIVILEGES_PER_FEATURE.PushNotifications.TemplateManagement,
        },
        children: [
          {
            path: '',
            redirectTo: 'templates',
            pathMatch: 'full',
          },
          {
            path: 'templates',
            loadComponent: () =>
              import('@features/admin-console/notifications/push-notification-templates-list/push-notification-templates-list.component').then(
                m => m.PushNotificationTemplatesListComponent
              ),
          },
        ],
      },
      {
        path: `${APP_ROUTES.APP_PUSH_NOTIFICATIONS_TEMPLATES}/:templateId`,
        canActivate: [portalAccessGuard],
        loadComponent: () =>
          import('@features/admin-console/notifications/push-notification-template/push-notification-template.component').then(
            m => m.PushNotificationTemplateComponent
          ),
      },
      {
        path: APP_ROUTES.LABELS,
        canActivate: [portalAccessGuard],
        loadComponent: () =>
          import('@features/admin-console/labels/label-list/labels-list.component').then(
            m => m.LabelsListComponent
          ),
      },
      {
        path: `${APP_ROUTES.LABELS}/namespaces/:namespaceId/keys`,
        canActivate: [portalAccessGuard],
        loadComponent: () =>
          import('@features/admin-console/labels/namespace-keys/namespace-keys.component').then(
            m => m.NamespaceKeysComponent
          ),
      },
      {
        path: `${APP_ROUTES.LABELS}/namespaces/:id`,
        canActivate: [portalAccessGuard],
        loadComponent: () =>
          import('@features/admin-console/labels/label-namespace-details/label-namespace-details.component').then(
            m => m.LabelNamespaceDetailsComponent
          ),
      },
      {
        path: `${APP_ROUTES.LABELS}/namespaces/:namespaceId/keys/:id`,
        canActivate: [portalAccessGuard],
        loadComponent: () =>
          import('@features/admin-console/labels/namespace-key-details/namespace-key-details.component').then(
            m => m.NamespaceKeyDetailsComponent
          ),
      },
      {
        path: `${APP_ROUTES.THREATS}/:id`,
        canActivate: [portalAccessGuard],
        loadComponent: () =>
          import('@features/admin-console/threats/threat-details/threat-details.component').then(
            m => m.ThreatDetailsComponent
          ),
      },
      // ---------------------- B2B PORTAL ROUTES ----------------------
      {
        path: APP_ROUTES.B2B_HOME,
        canActivate: [portalAccessGuard, b2bPortalAuthGuard],
        loadComponent: () =>
          import('@features/b2b-portal/home/components/home/home.component').then(
            m => m.HomeComponent
          ),
      },
      {
        path: APP_ROUTES.B2B_SERVICE,
        canActivate: [portalAccessGuard, b2bPortalAuthGuard],
        loadComponent: () =>
          import('@features/b2b-portal/service-center/components/service-center/service-center.component').then(
            m => m.ServiceCenterComponent
          ),
      },
      {
        path: APP_ROUTES.B2B_FUNDS_DATA,
        canActivate: [portalAccessGuard, b2bPortalAuthGuard],
        loadComponent: () =>
          import('@features/b2b-portal/funds-list/components/funds-list/funds-list.component').then(
            m => m.FundsListComponent
          ),
      },
      {
        path: APP_ROUTES.B2B_CUSTOMERS,
        canActivate: [portalAccessGuard, b2bPortalAuthGuard],
        loadComponent: () =>
          import('@features/b2b-portal/customers-list/components/customers-list/customers-list.component').then(
            m => m.CustomersListComponent
          ),
      },
      {
        path: `${APP_ROUTES.B2B_CUSTOMER_DETAILS}/:customerId/:depotNumber`,
        canActivate: [portalAccessGuard, b2bPortalAuthGuard],
        loadComponent: () =>
          import('@features/b2b-portal/customer-details/shell/customer-details-shell.component').then(
            m => m.CustomerDetailsShellComponent
          ),
      },
      {
        path: `${APP_ROUTES.B2B_ACCOUNT_STATEMENTS}/:customerId/:depotNumber`,
        canActivate: [portalAccessGuard, b2bPortalAuthGuard],
        loadComponent: () =>
          import('@features/b2b-portal/account-statements/components/account-statements/account-statements.component').then(
            m => m.AccountStatementsComponent
          ),
      },
      {
        path: `${APP_ROUTES.B2B_DEPOT_SLIPS}/:customerId/:depotNumber`,
        canActivate: [portalAccessGuard, b2bPortalAuthGuard],
        loadComponent: () =>
          import('@features/b2b-portal/depot-slips/components/depot-slips/depot-slips.component').then(
            m => m.DepotSlipsComponent
          ),
      },
      // ---------------------- AGENT FRONTEND ROUTES ----------------------
      {
        path: APP_ROUTES.AFE_CUSTOMERS,
        canActivate: [portalAccessGuard, msalGuard, permissionsGuard],
        loadComponent: () =>
          import('@features/agent-frontend/customer-search/components/customer-search-page/customer-search-page.component').then(
            m => m.CustomerSearchPageComponent
          ),
        title: 'Search Customer',
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.CustomerTab,
        },
      },
      {
        path: `${APP_ROUTES.AFE_CUSTOMERS}/entity/:entity/customer/:customerId`,
        canActivate: [
          customerIdNormalizerGuard,
          portalAccessGuard,
          msalGuard,
          permissionsGuard,
        ],
        loadComponent: () =>
          import('@features/agent-frontend/customer-landing-page/shell/customer-landing-page.component').then(
            m => m.CustomerLandingPageComponent
          ),
        resolve: { selectedInstitute: agentFrontendEntityResolver },
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.CustomerTab,
        },
      },
      {
        path: `${APP_ROUTES.AFE_CUSTOMERS}/entity/:entity/customer/:customerId/customer-profile`,
        canActivate: [
          customerIdNormalizerGuard,
          portalAccessGuard,
          msalGuard,
          permissionsGuard,
        ],
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () =>
          import('@features/agent-frontend/customer-details/shell/customer-details.component').then(
            m => m.CustomerDetailsComponent
          ),
        resolve: { selectedInstitute: agentFrontendEntityResolver },
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.CustomerTab,
        },
      },
      {
        path: `${APP_ROUTES.AFE_CUSTOMERS}/entity/:entity/customer/:customerId/card-management`,
        canActivate: [
          customerIdNormalizerGuard,
          portalAccessGuard,
          msalGuard,
          permissionsGuard,
        ],
        loadComponent: () =>
          import('@features/agent-frontend/card-management/shell/actions-overview/actions-overview.component').then(
            m => m.ActionsOverviewComponent
          ),
        resolve: { selectedInstitute: agentFrontendEntityResolver },
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.CustomerTab,
        },
      },
      {
        path: `${APP_ROUTES.AFE_CUSTOMERS}/entity/:entity/customer/:customerId/card-management/order-replacement`,
        canActivate: [
          customerIdNormalizerGuard,
          portalAccessGuard,
          msalGuard,
          permissionsGuard,
        ],
        loadComponent: () =>
          import('@features/agent-frontend/card-management/shell/order-replacement-form/order-replacement-form.component').then(
            m => m.OrderReplacementFormComponent
          ),
        resolve: { selectedInstitute: agentFrontendEntityResolver },
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.CustomerTab,
        },
      },
      {
        path: `${APP_ROUTES.AFE_CUSTOMERS}/entity/:entity/customer/:customerId/card-management/loss-theft`,
        canActivate: [
          customerIdNormalizerGuard,
          portalAccessGuard,
          msalGuard,
          permissionsGuard,
        ],
        loadComponent: () =>
          import('@features/agent-frontend/card-management/shell/lost-and-stolen-form/lost-and-stolen-form.component').then(
            m => m.LostAndStolenFormComponent
          ),
        resolve: { selectedInstitute: agentFrontendEntityResolver },
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.CustomerTab,
        },
      },
      {
        path: `${APP_ROUTES.AFE_CUSTOMERS}/entity/:entity/customer/:customerId/card-management/card-pin`,
        canActivate: [
          customerIdNormalizerGuard,
          portalAccessGuard,
          msalGuard,
          permissionsGuard,
        ],
        loadComponent: () =>
          import('@features/agent-frontend/feature-not-implemented/feature-not-implemented.component').then(
            m => m.FeatureNotImplementedComponent
          ),
        title: 'Card PIN - Not Implemented',
        resolve: { selectedInstitute: agentFrontendEntityResolver },
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.CustomerTab,
        },
      },
      {
        path: `${APP_ROUTES.AFE_CUSTOMERS}/entity/:entity/customer/:customerId/card-management/temporary-block`,
        canActivate: [
          customerIdNormalizerGuard,
          portalAccessGuard,
          msalGuard,
          permissionsGuard,
        ],
        loadComponent: () =>
          import('@features/agent-frontend/feature-not-implemented/feature-not-implemented.component').then(
            m => m.FeatureNotImplementedComponent
          ),
        title: 'Temporary Block - Not Implemented',
        resolve: { selectedInstitute: agentFrontendEntityResolver },
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.CustomerTab,
        },
      },
      {
        path: `${APP_ROUTES.AFE_CUSTOMERS}/entity/:entity/customer/:customerId/card-management/activate-prepaid`,
        canActivate: [
          customerIdNormalizerGuard,
          portalAccessGuard,
          msalGuard,
          permissionsGuard,
        ],
        loadComponent: () =>
          import('@features/agent-frontend/feature-not-implemented/feature-not-implemented.component').then(
            m => m.FeatureNotImplementedComponent
          ),
        title: 'Activate Prepaid - Not Implemented',
        resolve: { selectedInstitute: agentFrontendEntityResolver },
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.CustomerTab,
        },
      },
      {
        path: `${APP_ROUTES.AFE_CUSTOMERS}/entity/:entity/customer/:customerId/account-management`,
        canActivate: [
          customerIdNormalizerGuard,
          portalAccessGuard,
          msalGuard,
          permissionsGuard,
        ],
        loadComponent: () =>
          import('@features/agent-frontend/feature-not-implemented/feature-not-implemented.component').then(
            m => m.FeatureNotImplementedComponent
          ),
        title: 'Account Management - Not Implemented',
        resolve: { selectedInstitute: agentFrontendEntityResolver },
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.CustomerTab,
        },
      },
      {
        path: `${APP_ROUTES.AFE_CUSTOMERS}/entity/:entity/customer/:customerId/transactions`,
        canActivate: [
          customerIdNormalizerGuard,
          portalAccessGuard,
          msalGuard,
          permissionsGuard,
        ],
        loadComponent: () =>
          import('@features/agent-frontend/transactions/shell/transactions-shell.component').then(
            m => m.TransactionsShellComponent
          ),
        resolve: { selectedInstitute: agentFrontendEntityResolver },
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.CustomerTab,
        },
      },
      {
        path: `${APP_ROUTES.AFE_CUSTOMERS}/entity/:entity/customer/:customerId/history`,
        canActivate: [
          customerIdNormalizerGuard,
          portalAccessGuard,
          msalGuard,
          permissionsGuard,
        ],
        loadComponent: () =>
          import('@features/agent-frontend/feature-not-implemented/feature-not-implemented.component').then(
            m => m.FeatureNotImplementedComponent
          ),
        title: 'History - Not Implemented',
        resolve: { selectedInstitute: agentFrontendEntityResolver },
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.CustomerTab,
        },
      },
      {
        path: `${APP_ROUTES.AFE_CUSTOMERS}/entity/:entity/customer/:customerId/interactions`,
        canActivate: [
          customerIdNormalizerGuard,
          portalAccessGuard,
          msalGuard,
          permissionsGuard,
        ],
        loadComponent: () =>
          import('@features/agent-frontend/feature-not-implemented/feature-not-implemented.component').then(
            m => m.FeatureNotImplementedComponent
          ),
        title: 'Interactions - Not Implemented',
        resolve: { selectedInstitute: agentFrontendEntityResolver },
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.CustomerTab,
        },
      },
      {
        path: `${APP_ROUTES.AFE_CUSTOMERS}/entity/:entity/customer/:customerId/digital-user`,
        canActivate: [
          customerIdNormalizerGuard,
          portalAccessGuard,
          msalGuard,
          permissionsGuard,
        ],
        loadComponent: () =>
          import('@features/agent-frontend/feature-not-implemented/feature-not-implemented.component').then(
            m => m.FeatureNotImplementedComponent
          ),
        title: 'Digital User - Not Implemented',
        resolve: { selectedInstitute: agentFrontendEntityResolver },
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.CustomerTab,
        },
      },
      {
        path: `${APP_ROUTES.AFE_CUSTOMERS}/entity/:entity/customer/:customerId/enquiries`,
        canActivate: [
          customerIdNormalizerGuard,
          portalAccessGuard,
          msalGuard,
          permissionsGuard,
        ],
        loadComponent: () =>
          import('@features/agent-frontend/feature-not-implemented/feature-not-implemented.component').then(
            m => m.FeatureNotImplementedComponent
          ),
        title: 'Enquiries - Not Implemented',
        resolve: { selectedInstitute: agentFrontendEntityResolver },
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.CustomerTab,
        },
      },
      {
        path: `${APP_ROUTES.AFE_CUSTOMERS}/entity/:entity/customer/:customerId/product-information`,
        canActivate: [
          customerIdNormalizerGuard,
          portalAccessGuard,
          msalGuard,
          permissionsGuard,
        ],
        loadComponent: () =>
          import('@features/agent-frontend/feature-not-implemented/feature-not-implemented.component').then(
            m => m.FeatureNotImplementedComponent
          ),
        title: 'Product Information - Not Implemented',
        resolve: { selectedInstitute: agentFrontendEntityResolver },
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.CustomerTab,
        },
      },
      {
        path: `${APP_ROUTES.AFE_CUSTOMERS}/feature-not-implemented`,
        canActivate: [portalAccessGuard],
        loadComponent: () =>
          import('@features/agent-frontend/feature-not-implemented/feature-not-implemented.component').then(
            m => m.FeatureNotImplementedComponent
          ),
        title: 'Feature Not Implemented',
        data: {
          requiredPrivileges: PRIVILEGES_PER_FEATURE.CustomerTab,
        },
      },
      {
        path: `${APP_ROUTES.AFE_CUSTOMERS}/entity/:entity/customer/:customerId/digital-user/user/:userId`,
        canActivate: [
          customerIdNormalizerGuard,
          portalAccessGuard,
          permissionsGuard,
          msalGuard,
        ],
        loadComponent: () =>
          import('@features/admin-console/users/user-details/user-details.component').then(
            m => m.UserDetailsComponent
          ),
        resolve: { selectedInstitute: agentFrontendEntityResolver },
      },
    ],
  },
  {
    path: '**',
    redirectTo: environment.homeRoute,
  },
];
